import type { GridRowGroupingModel, GridSortModel } from '@mui/x-data-grid-premium';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../../../../i18n/I18n';
import { EMPTY_GRID_SETTINGS_OPTIONS } from '../../constants';
import type { GridSettingsBindings, GridSettingsPreference, UseGridSettingsParams } from '../../types';
import { GridSettingsGear } from '../components/GridSettingsGear';
import { GridSettingsModal } from '../components/GridSettingsModal';
import {
    areGridSettingsEqual,
    createFlatSortComparator,
    createGroupingSortComparator,
    findSortOption,
    groupedFieldsFromPreference,
    isCustomSortId,
    preferenceToRowGroupingModel,
    preferenceToSortModel,
    prefillEmptyValueOrders,
    resolveSortFields,
    toDefaultPreference,
    useStableReference
} from '../utils';

/**
 * Grid settings for a DataGrid: gear, modal, and the sort/group models to apply.
 * Call this from the grid and pass `sortModel`, `rowGroupingModel`, and `onSortModelChange`
 * through to the grid when `enabled` is true. See `UseGridSettingsParams` for what each
 * argument does when set or omitted.
 */
export function useGridSettings({
    enabled,
    showAutosize,
    settingsEntityName,
    config,
    rows,
    defaultSortModel,
    defaultRowGroupingModel,
    onSortModelChange,
    autosizeEnabled,
    onAutosizeEnabledChange
}: UseGridSettingsParams): GridSettingsBindings {
    const t = useI18n();
    const showSettings = enabled || showAutosize;
    const settingsTitle = settingsEntityName
        ? t('entity.settings.title', { entity: settingsEntityName })
        : t('settings.title');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);
    const [committedPreference, setCommittedPreference] = useState<GridSettingsPreference | null>(null);
    const [draftPreference, setDraftPreference] = useState<GridSettingsPreference | null>(null);
    const [gridSettingsRevision, setGridSettingsRevision] = useState(0);

    const nextDefaultPreference = toDefaultPreference(config, defaultSortModel, defaultRowGroupingModel);
    const defaultPreference = useStableReference(nextDefaultPreference);
    // A modal save is a draft on the current view. Choosing another view drops that draft.
    const appliedPreference = draftPreference ?? committedPreference ?? defaultPreference;

    const applyGridSettings = useCallback((next: GridSettingsPreference | null) => {
        setDraftPreference(null);
        setCommittedPreference(next);
        setGridSettingsRevision((revision) => revision + 1);
    }, []);
    const applyDraftGridSettings = useCallback((next: GridSettingsPreference) => {
        setDraftPreference(next);
        setGridSettingsRevision((revision) => revision + 1);
    }, []);
    const appliedPreferenceRef = useRef(appliedPreference);
    appliedPreferenceRef.current = appliedPreference;
    const getCurrentGridSettings = useCallback(() => {
        return appliedPreferenceRef.current;
    }, []);
    const sortFields = useMemo(() => resolveSortFields(config?.sortFields, rows), [config?.sortFields, rows]);

    const nextSortModel = enabled
        ? (preferenceToSortModel(appliedPreference, config) ?? defaultSortModel)
        : defaultSortModel;
    const stableSortModel = useStableReference(nextSortModel);
    // MUI reapplies client sort only when the sort model reference changes. Value-order
    // edits keep the same field and direction, so copy the model when settings are applied.
    const appliedSortModel = useMemo(
        () => (stableSortModel ? [...stableSortModel] : stableSortModel),
        [gridSettingsRevision, stableSortModel]
    );
    const nextRowGroupingModel: GridRowGroupingModel | undefined = enabled
        ? preferenceToRowGroupingModel(appliedPreference)
        : defaultRowGroupingModel;
    const stableRowGroupingModel = useStableReference(nextRowGroupingModel);
    // MUI skips grouping updates when the array reference is unchanged. Copy it whenever
    // settings are applied so abandoning a draft restores the previous groups.
    const appliedRowGroupingModel = useMemo(
        () => [...(stableRowGroupingModel ?? [])],
        [gridSettingsRevision, stableRowGroupingModel]
    );

    const inGroupOption = findSortOption(config?.inGroupSortOptions, appliedPreference.inGroupSortBy);
    const groupedFields = useStableReference(groupedFieldsFromPreference(appliedPreference));
    const hasGroupedFields = groupedFields.length > 0;
    const hasCustomSortFields =
        enabled && isCustomSortId(appliedPreference.sortBy) && Boolean(appliedPreference.groupFields?.length);
    const ungroupedSortFields = useStableReference(
        (appliedPreference.groupFields ?? []).filter((fieldId) => !groupedFields.includes(fieldId))
    );
    const groupingSortComparator = useMemo(() => {
        if (!hasCustomSortFields || !hasGroupedFields) {
            return undefined;
        }

        return createGroupingSortComparator(appliedPreference.groupValueOrders, inGroupOption, ungroupedSortFields);
    }, [appliedPreference.groupValueOrders, hasCustomSortFields, hasGroupedFields, inGroupOption, ungroupedSortFields]);
    const flatSortComparator = useMemo(() => {
        if (!hasCustomSortFields || hasGroupedFields) {
            return undefined;
        }

        return createFlatSortComparator(
            appliedPreference.groupFields ?? [],
            appliedPreference.groupValueOrders,
            inGroupOption
        );
    }, [
        appliedPreference.groupFields,
        appliedPreference.groupValueOrders,
        hasCustomSortFields,
        hasGroupedFields,
        inGroupOption
    ]);
    const flatSortField = hasCustomSortFields && !hasGroupedFields ? appliedPreference.groupFields?.[0] : undefined;

    const handleSortModelChange = useCallback(
        (model: GridSortModel) => {
            onSortModelChange?.(model);
        },
        [onSortModelChange]
    );

    const handleSave = useCallback(
        (nextPreference: GridSettingsPreference, nextAutosizeEnabled: boolean) => {
            const displayedPreference = prefillEmptyValueOrders(appliedPreference, sortFields);
            if (
                enabled &&
                !areGridSettingsEqual(nextPreference, appliedPreference) &&
                !areGridSettingsEqual(nextPreference, displayedPreference)
            ) {
                applyDraftGridSettings(nextPreference);
            }
            if (showAutosize && nextAutosizeEnabled !== autosizeEnabled) {
                onAutosizeEnabledChange(nextAutosizeEnabled);
            }
            closeModal();
        },
        [
            appliedPreference,
            applyDraftGridSettings,
            autosizeEnabled,
            closeModal,
            enabled,
            onAutosizeEnabledChange,
            sortFields,
            showAutosize
        ]
    );

    const toolbarControl = useMemo(
        () => (showSettings ? <GridSettingsGear onOpen={openModal} /> : undefined),
        [openModal, showSettings]
    );

    const modal = showSettings ? (
        <GridSettingsModal
            open={isModalOpen}
            title={settingsTitle}
            entityName={settingsEntityName}
            showSortGroup={enabled}
            showAutosize={showAutosize}
            sortOptions={config?.sortOptions ?? EMPTY_GRID_SETTINGS_OPTIONS}
            sortFields={sortFields}
            inGroupSortOptions={config?.inGroupSortOptions ?? EMPTY_GRID_SETTINGS_OPTIONS}
            preference={appliedPreference}
            autosizeEnabled={autosizeEnabled}
            onClose={closeModal}
            onSave={handleSave}
        />
    ) : null;

    return {
        showSettings,
        sortModel: appliedSortModel,
        rowGroupingModel: appliedRowGroupingModel,
        groupingSortComparator,
        flatSortComparator,
        flatSortField,
        onSortModelChange: handleSortModelChange,
        appliedPreference,
        defaultPreference,
        applyGridSettings,
        getCurrentGridSettings,
        gridSettingsRevision,
        toolbarControl,
        modal
    };
}
